import React, { useCallback, useMemo, useState } from 'react';
import {
  createQColumnHelper,
  DataProvider,
  Filtering,
  Pagination,
  QBodyLayout,
  QButton,
  QDataTable,
  QEmptyState,
  QHeading,
  QSelectItem,
  QStack,
  QText,
  Sorting,
  useCurrentUser,
  useFeatureFlag,
} from '@qualio/ui-components';
import { createSearchParams, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Template, TEMPLATE_STATUSES } from '../../types';
import { useTemplates } from '../../hooks/useTemplates';
import { isInheritedTemplate } from '../../hooks/useTemplateActions';
import { getDateTimeStr, getOwnerText, getTagText } from '../../utils/textUtil';
import ActiveStatusFilter from '../TemplateListView/ActiveStatusFilter';
import { CreateTemplateModal } from '../../components/CreateTemplateModal/CreateTemplateModal';
import { TemplateStatusFilter } from './TemplateStatusFilter';
import { TemplateAccountFilter } from './TemplateAccountFilter';
import { TemplateOwnerFilter, UNASSIGNED_OWNER } from './TemplateOwnerFilter';
import { ActiveOwnerFilter } from './ActiveOwnerFilter';
import { InheritedTemplateIcon } from '../../components/shared/InheritedTemplateIcon/InheritedTemplateIcon';
import { useTags } from '../../hooks/useTags';
import { Tag } from '../../types/tag';

const columnHelper = createQColumnHelper<Template>();
const CREATE_TEMPLATE_NEW_ROUTE = '/doc-templates/new';
const DEFAULT_STATUS = 'allOpenStatuses';
const STATUS_FILTER_VALUES = [DEFAULT_STATUS, ...TEMPLATE_STATUSES] as const;

type StatusFilterValue = typeof STATUS_FILTER_VALUES[number];

const isStatusFilterValue = (value: string): value is StatusFilterValue =>
  STATUS_FILTER_VALUES.includes(value as StatusFilterValue);

const filterBySearchTerm = (item: Template, searchTerm: string | undefined): boolean => {
  if (!searchTerm) return true;
  const term = searchTerm.toLowerCase();
  return item.name.toLowerCase().includes(term) || item.prefix.toLowerCase().includes(term);
};

const renderInheritedTemplateIcon = (template: Template) =>
  template.parent ? <InheritedTemplateIcon templateId={template.id} parentName={template.parent.name} /> : null;

const accountNameOf = (item: Template, ownAccountName: string): string => item.parent?.name ?? ownAccountName;

const matchesAccount = (item: Template, ownAccountName: string, account: string | undefined): boolean =>
  !account || accountNameOf(item, ownAccountName) === account;

const matchesOwner = (item: Template, owner: string | undefined): boolean => {
  if (!owner) return true;
  if (isInheritedTemplate(item)) return false;
  return owner === UNASSIGNED_OWNER ? !item.owner : String(item.owner?.user_id) === owner;
};

const defaultTagsOf = (template: Template, tags: Tag[]) =>
  tags
    .filter((tag) => template.tag_ids?.includes(tag.id))
    .map((tag) => (tag.group_ids.length ? { label: tag.name, leftIcon: 'Lock' as const } : tag.name));

const ActiveAccountFilter = ({ value }: { value: unknown }) => <QText>{typeof value === 'string' ? value : ''}</QText>;

// Rendered inside the FilterProvider so it can read the applied status filter.
const TemplateTable: React.FC<{ statusOptions: QSelectItem[] }> = ({ statusOptions }) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const filtering = Filtering.useFiltering();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const selectedStatus: string | undefined = filtering.filters.status?.value;
  const templateStatuses = selectedStatus ? [selectedStatus] : [...TEMPLATE_STATUSES];
  const shouldSortByStatus = !selectedStatus || selectedStatus === DEFAULT_STATUS;

  const { data, isLoading } = useTemplates(templateStatuses);
  const templateDefaultTags = Boolean(useFeatureFlag()['template-default-tags']);
  const { data: tags = [] } = useTags();

  const parentAccountName = useMemo(() => data?.find((template) => template.parent)?.parent?.name, [data]);

  const accountOptions = useMemo(
    () =>
      parentAccountName
        ? [currentUser.companyName, parentAccountName].map((name) => ({ value: name, label: name } as QSelectItem))
        : [],
    [currentUser.companyName, parentAccountName],
  );

  const filterTemplates = useCallback(
    (
      item: Template,
      searchTerm: string | undefined,
      filters: Filtering.ResolvedFilters<Filtering.FilterDefinitions> | undefined,
    ): boolean =>
      matchesAccount(item, currentUser.companyName, filters?.account?.value) &&
      matchesOwner(item, filters?.owner?.value) &&
      filterBySearchTerm(item, searchTerm),
    [currentUser.companyName],
  );

  const handleTitleClick = useCallback(
    (clickedTemplate: Template) => {
      const page = searchParams.get('page') || '0';
      const orderBy = searchParams.get('orderBy') || '-modified_time';
      navigate({
        pathname: `/doc-templates/${clickedTemplate.id}`,
        search: `?${createSearchParams({ page, orderBy })}`,
      });
    },
    [navigate, searchParams],
  );

  const columns = useMemo(
    () => [
      columnHelper.id('prefix', {
        header: t('template.list.table.columns.prefix'),
        id: 'prefix',
        width: '200px',
      }),
      columnHelper.textAction('name', handleTitleClick, {
        header: t('template.list.table.columns.title'),
        id: 'name',
        minWidth: '305px',
        append: renderInheritedTemplateIcon,
        appendPlacement: 'end',
      }),
      columnHelper.text(getOwnerText, {
        header: t('template.list.table.columns.owner'),
        id: 'owner',
        width: '200px',
        enableSorting: false,
      }),
      // The tag cell measures its own chips and collapses the overflow into a
      // "+N" tooltip, so the accessor only has to return the tags.
      ...(templateDefaultTags
        ? [
            columnHelper.tag((template) => defaultTagsOf(template, tags), {
              header: t('template.list.table.columns.defaultTags'),
              id: 'default_tags',
              width: '250px',
              enableSorting: false,
              statuses: { '*': 'default' },
            }),
          ]
        : []),
      columnHelper.text('version', {
        header: t('template.list.table.columns.version'),
        id: 'version',
        width: '160px',
      }),
      columnHelper.text((template) => getDateTimeStr(template.modified_time), {
        header: t('template.list.table.columns.lastModified'),
        id: 'modified_time',
        width: '200px',
      }),
      columnHelper.status((template) => getTagText(template.status), {
        header: t('template.list.table.columns.status'),
        id: 'status',
        width: '200px',
        enableSorting: shouldSortByStatus,
        statuses: {
          Effective: 'green',
          Archived: 'red',
          Draft: 'gray',
          Superseded: 'red',
        },
      }),
    ],
    [handleTitleClick, shouldSortByStatus, t, templateDefaultTags, tags],
  );

  return (
    <Sorting.DefaultSortingProvider
      initialSort={shouldSortByStatus ? { column: 'status' } : { column: 'modified_time', descending: true }}
    >
      <DataProvider.Fixed data={data ?? []} filterOption={filterTemplates} isLoading={isLoading}>
        <QDataTable columns={columns} data-cy="template-list-table-v2" data-testid="template-list-table-v2">
          <Filtering.FormContent>
            <QStack direction="column" spacing={4}>
              <TemplateStatusFilter statusOptions={statusOptions} />
              <TemplateOwnerFilter />
              {accountOptions.length > 0 ? <TemplateAccountFilter accountOptions={accountOptions} /> : null}
            </QStack>
          </Filtering.FormContent>
          <QEmptyState
            title={t('template.list.emptyState.title')}
            subtitle={t('template.list.emptyState.subtitle')}
            data-cy="templates-table-empty-state"
            data-testid="templates-table-empty-state"
          />
        </QDataTable>
      </DataProvider.Fixed>
    </Sorting.DefaultSortingProvider>
  );
};

export const TemplateListViewV2: React.FC = () => {
  const { t } = useTranslation();
  const routeMatch = useMatch(CREATE_TEMPLATE_NEW_ROUTE);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(
    routeMatch?.pathnameBase === CREATE_TEMPLATE_NEW_ROUTE,
  );

  const handleCreateTemplateClick = useCallback(() => {
    setShowCreateTemplateModal(true);
  }, [setShowCreateTemplateModal]);

  const statusMap: Record<string, string> = useMemo(
    () => ({
      allOpenStatuses: t('template.list.filters.status.all'),
      draft: t('template.list.filters.status.draft'),
      effective: t('template.list.filters.status.effective'),
      superseded: t('template.list.filters.status.superseded'),
      archived: t('template.list.filters.status.archived'),
    }),
    [t],
  );

  const statusOptions = useMemo(
    () => Object.keys(statusMap).map((value) => ({ value, label: statusMap[value] } as QSelectItem)),
    [statusMap],
  );

  const activeStatusFilterRender = useCallback(
    (value: unknown) => <ActiveStatusFilter value={value} statusMap={statusMap} />,
    [statusMap],
  );

  const filterDefinitions: Filtering.FilterDefinitions = useMemo(
    () => ({
      status: {
        label: t('template.list.filters.status.label'),
        schema: Filtering.schemas.StringSchema((status) => status.refine(isStatusFilterValue)),
        activeRender: activeStatusFilterRender,
        defaultValue: DEFAULT_STATUS,
      },
      owner: {
        label: t('template.list.filters.owner.label'),
        schema: Filtering.schemas.StringSchema().nullable(),
        activeRender: (value: unknown) => <ActiveOwnerFilter value={value} />,
      },
      account: {
        label: t('template.list.filters.account.label'),
        schema: Filtering.schemas.StringSchema().nullable(),
        activeRender: (value: unknown) => <ActiveAccountFilter value={value} />,
      },
    }),
    [activeStatusFilterRender, t],
  );

  return (
    <QBodyLayout.Default>
      <QStack direction="row" justify="space-between">
        <QHeading size="lg" mb={6} data-cy="templates-page-heading">
          {t('template.list.title')}
        </QHeading>
        <QButton onClick={handleCreateTemplateClick} data-cy="create-draft-template-modal">
          {t('template.list.createButton')}
        </QButton>
      </QStack>
      <Pagination.Auto pageSizeKey="size" pageIndexKey="page" clientSide>
        <Filtering.FilterProvider
          definitions={filterDefinitions}
          persistanceKey="document-templates-list-table-v2"
          searchTermKey="search"
        >
          <TemplateTable statusOptions={statusOptions} />
        </Filtering.FilterProvider>
      </Pagination.Auto>
      {showCreateTemplateModal ? <CreateTemplateModal onClose={setShowCreateTemplateModal} /> : null}
    </QBodyLayout.Default>
  );
};
